'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Shield, User, UtensilsCrossed, X } from 'lucide-react';

type QuickRole = 'admin' | 'staff';

type DemoQuickAccessProps = {
  /** When known from parent (menu/join payload). If omitted, fetches status. */
  enabled?: boolean;
  slug?: string | null;
  variant?: 'auth' | 'floating';
  className?: string;
};

async function runQuickLogin(role: QuickRole): Promise<{ ok: boolean; error?: string }> {
  const res = await fetch('/api/auth/quick-login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    return { ok: false, error: data.error || 'Acesso rapido indisponivel.' };
  }
  return { ok: true };
}

/**
 * Demo / quick-access entry points for tenant surfaces (auth, PWA, mesa, waiter gate).
 * Visibility is controlled by restaurants.settings.features.demo_quick_access.
 * Never shown on super-admin.
 */
export function DemoQuickAccess({
  enabled: enabledProp,
  slug,
  variant = 'floating',
  className = '',
}: DemoQuickAccessProps) {
  const router = useRouter();
  const [enabled, setEnabled] = useState(Boolean(enabledProp));
  const [resolvedSlug, setResolvedSlug] = useState(slug ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof enabledProp === 'boolean') {
      setEnabled(enabledProp);
      return;
    }
    let cancelled = false;
    fetch('/api/auth/quick-access', { cache: 'no-store' })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setEnabled(Boolean(data.enabled));
        if (data.slug) setResolvedSlug(String(data.slug));
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => { cancelled = true; };
  }, [enabledProp]);

  useEffect(() => {
    if (slug) setResolvedSlug(slug);
  }, [slug]);

  if (!enabled || dismissed) return null;

  const go = async (role: QuickRole) => {
    setBusy(role);
    setError(null);
    const result = await runQuickLogin(role);
    if (!result.ok) {
      setError(result.error || 'Falha no acesso rapido.');
      setBusy(null);
      return;
    }
    if (role === 'admin') {
      router.push('/admin');
      return;
    }
    const targetSlug = resolvedSlug || slug;
    router.push(targetSlug ? `/waiter/${targetSlug}` : '/auth');
  };

  if (variant === 'auth') {
    return (
      <div className={`space-y-3 ${className}`}>
        <button
          type="button"
          onClick={() => void go('admin')}
          disabled={busy === 'admin'}
          className="auth-role-btn auth-role-btn-primary"
        >
          <span className="flex items-center gap-2"><Shield className="w-4 h-4" /> Entrar como admin</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => void go('staff')}
          disabled={busy === 'staff'}
          className="auth-role-btn auth-role-btn-secondary"
        >
          <span className="flex items-center gap-2"><User className="w-4 h-4" /> Entrar como funcionario</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        {error ? <p className="text-sm text-danger-text">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className={`fixed bottom-4 left-4 right-4 z-[80] mx-auto max-w-lg ${className}`}>
      <div className="rounded-2xl border border-ink-900/10 bg-ink-900/95 p-3 text-white shadow-xl backdrop-blur">
        <div className="mb-2 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Acesso demo</p>
            <p className="text-sm text-white/85">Entrar rapidamente noutro perfil deste restaurante.</p>
          </div>
          <button
            type="button"
            aria-label="Fechar acesso demo"
            onClick={() => setDismissed(true)}
            className="rounded-lg p-1 text-white/60 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => void go('admin')}
            disabled={Boolean(busy)}
            className="flex items-center justify-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-semibold text-ink-900 disabled:opacity-60"
          >
            <Shield className="h-4 w-4" />
            {busy === 'admin' ? 'A entrar…' : 'Admin'}
          </button>
          <button
            type="button"
            onClick={() => void go('staff')}
            disabled={Boolean(busy)}
            className="flex items-center justify-center gap-2 rounded-xl bg-white/15 px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            <UtensilsCrossed className="h-4 w-4" />
            {busy === 'staff' ? 'A entrar…' : 'Funcionario'}
          </button>
        </div>
        {error ? <p className="mt-2 text-xs text-amber-200">{error}</p> : null}
      </div>
    </div>
  );
}
